import orderBy from 'lodash/orderBy'

import arriereBackoffice from '@avantstay/arriere-clients/dist/arriereBackoffice'

import {
  RemoveRoomProps,
  UpdateRoomsProps,
  UpdateSingleRoomProps,
} from '@arriere/types/PropertyLayout.types'

export const layoutImagesFragment = {
  id: true,
  extension: true,
  size: true,
  fileName: true,
  url: true,
}

const roomsFragment = {
  id: true,
  listOrder: true,
  name: true,
  description: true,
  kind: true,
  maxOccupancy: true,
  bathroomId: true,
  bathroomKind: true,
  images: {
    __args: { useNewVersion: true },
    ...layoutImagesFragment,
  },
  sleepingPlaces: {
    amenity: {
      id: true,
      name: true,
    },
    roomId: true,
    quantity: true,
  },
  roomAmenities: {
    amenity: {
      id: true,
      name: true,
      tags: true,
    },
    images: {
      asset: {
        id: true,
        fileName: true,
        url: true,
      },
    },
    roomId: true,
    quantity: true,
  },
  bathroomAmenities: {
    amenity: {
      id: true,
      name: true,
      tags: true,
    },
    images: {
      asset: {
        id: true,
        fileName: true,
        url: true,
      },
    },
    roomId: true,
    quantity: true,
  },
}

export const getPropertyLayoutLegacyPhotos = async (homeId: string) => {
  return arriereBackoffice.queries.home
    .raw({
      __alias: 'getPropertyLayoutLegacyPhotos',
      __args: { homeId },
      rooms: {
        id: true,
        images: {
          __args: { useNewVersion: false },
          ...layoutImagesFragment,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return data.rooms
    })
}

export const getPropertyLayout = async (homeId: string) => {
  return arriereBackoffice.queries.home
    .raw({
      __alias: 'getPropertyLayout',
      __args: { homeId },
      rooms: {
        ...roomsFragment,
      },
    })
    .then(async ({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      const legacyPhotos = await getPropertyLayoutLegacyPhotos(homeId)

      return data.rooms.map(room => ({
        ...room,
        legacyImages: legacyPhotos.find(legacyRoom => legacyRoom.id === room.id)?.images,
      }))
    })
}

const amenitiesFragment = {
  id: true,
  name: true,
  tags: true,
}

export const getRoomsAmenities = async () => {
  return arriereBackoffice.queries.roomScopedAmenities
    .raw({
      __alias: 'getRoomsAmenities',
      sleepingPlaces: {
        ...amenitiesFragment,
      },
      roomAmenities: {
        ...amenitiesFragment,
      },
      bathroomAmenities: {
        ...amenitiesFragment,
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return data
    })
}

export const getSleepingArrangements = async () => {
  return arriereBackoffice.queries.roomScopedAmenities
    .raw({
      __alias: 'getRoomsAmenities',
      sleepingPlaces: {
        ...amenitiesFragment,
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return data.sleepingPlaces
    })
}

export const getPhotoGalleryData = async (homeId: string) => {
  return arriereBackoffice.queries.home
    .raw({
      __alias: 'getPhotoGalleryData',
      __args: { homeId },
      images: {
        listOrder: true,
        description: true,
        asset: {
          id: true,
          extension: true,
          size: true,
          fileName: true,
          url: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return data.images
    })
}

export const getRoomCategories = async () => {
  return arriereBackoffice.queries.roomCategoryKinds
    .raw({
      roomCategory: true,
      roomKinds: true,
    })
    .then(async ({ data, errors }) => {
      if (errors && errors?.length) {
        throw new Error(errors[0].message)
      }

      return orderBy(data, 'roomCategory', 'asc')
    })
}

// Mutations
export const updateHomeRooms = async ({ homeId, rooms }: UpdateRoomsProps) => {
  return arriereBackoffice.mutations.updateHomeRooms({
    __args: { homeId, rooms, useNewVersion: true },
    ...roomsFragment,
  })
}

export const updateSingleHomeRoom = async ({ homeId, room }: UpdateSingleRoomProps) => {
  return arriereBackoffice.mutations.updateHomeRoom({
    __args: { homeId, room },
    id: true,
  })
}

export const removeHomeRoom = ({ homeId, roomId }: RemoveRoomProps) => {
  return arriereBackoffice.mutations.removeHomeRoom({ __args: { homeId, roomId } })
}
